import * as React from 'react';

import { getStorageCachedURL } from '@gitbook/app-database';
import type { BlockFileData } from '@gitbook/doc-core';
import { useHiveRevision } from '@gitbook/hive-react';
import { MediaCard } from '@gitbook/react-uikit';
import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { getFileType, getHumanFileSize } from '../../../utils';
import { FileIcon } from '../../Files';
import { useInsertFiles } from '../../InsertFilesDialog';
import { EditableCaption, useCaption } from '../caption';
import {
    BlockLoadingFallback,
    BlockWrapper,
    useBlockPaletteFactory,
    VoidBlockFrame,
} from '../common';

/**
 * Renderer for a block file (in an images gallery or files)
 */
export function BlockFile(props: BlockProps) {
    const { node, editor } = props;
    const insertFiles = useInsertFiles();

    /** Prompt user to select a new file for this block */
    const onReplace = async () => {
        const [ref] = await insertFiles.prompt({
            title: 'Replace file',
        });

        if (ref?.kind !== 'file') {
            return;
        }

        editor.change((change) => {
            change.insertDataByKey(node.key, ['ref'], ref);
        });
    };

    const caption = useCaption(node, editor);
    const palette = useBlockPaletteFactory({
        nodeKey: node.key,
        createExtraGroups: () => [
            {
                key: 'types',
                commands: () => {
                    return [
                        {
                            key: 'files',
                            title: 'Replace file…',
                            icon: Icon.Paperclip,
                            trigger: () => {
                                onReplace();
                            },
                        },
                        {
                            key: 'caption',
                            title: caption.hasCaption ? 'Edit caption' : 'Add caption',
                            icon: Icon.Caption,
                            trigger: () => {
                                caption.selectCaption();
                            },
                        },
                        caption.hasCaption
                            ? {
                                  key: 'caption-remove',
                                  title: 'Remove caption',
                                  icon: Icon.CaptionRemove,
                                  trigger: () => {
                                      caption.removeCaption();
                                  },
                              }
                            : null,
                    ].filter(filterOutNullable);
                },
            },
        ],
    });

    return (
        <BlockWrapper {...props} palette={palette}>
            <VoidBlockFrame {...props}>
                <React.Suspense fallback={<BlockLoadingFallback />}>
                    <FileCard {...props} onReplace={onReplace} />
                </React.Suspense>
            </VoidBlockFrame>
            <EditableCaption {...props} />
        </BlockWrapper>
    );
}

function FileCard(props: BlockProps & { onReplace?: () => void }) {
    const { node, editor, onReplace } = props;

    const { ref } = node.data.toFrozenObject() as BlockFileData;

    const { revision } = useHiveRevision();
    const file = revision.files[ref.file];
    const href = file ? getStorageCachedURL(file.downloadURL) : undefined;

    return (
        <MediaCard
            icon={<FileIcon file={file} />}
            title={file ? file.name : 'File has been deleted'}
            details={
                file ? (
                    <div className="flex items-center gap-1">
                        {getFileType(file)}
                        <span>•</span>
                        {getHumanFileSize(file.size)}
                    </div>
                ) : undefined
            }
            buttons={
                href && file ? (
                    <>
                        <Button
                            kind="secondary"
                            size="small"
                            leadingIcon={Icon.Download}
                            onPress={async () => {
                                await forceDownload(href, file.name);
                            }}
                            label="Download"
                        />
                        <Button
                            kind="secondary"
                            size="small"
                            leadingIcon={Icon.ExternalLink}
                            onPress={async () => {
                                window.open(href, '_blank', 'noopener');
                            }}
                            label="Open"
                        />
                    </>
                ) : !editor.readOnly && onReplace ? (
                    <Button
                        kind="secondary"
                        size="small"
                        leadingIcon={Icon.Download}
                        onPress={() => {
                            onReplace();
                        }}
                        label="Replace"
                    />
                ) : null
            }
        />
    );
}

/**
 * Force download a file from a given URL with a specified filename.
 */
async function forceDownload(url: string, filename: string) {
    const response = await fetch(url);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(objectUrl);
}
